import { TokenManager } from "@/utils/api-client-factory";

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  "https://main-api-production-1bb6.up.railway.app/api";

export type NotificationCategory = "PRICE_ALERTS" | "TRADES" | "LISTINGS" | "DELISTINGS" | "SYSTEM";

export interface NotificationItem {
  /** Row id, or `alert:<id>` for a listing / delisting. */
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  /** Mini app path the View button opens. */
  link: string | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationInbox {
  items: NotificationItem[];
  unread: number;
}

export interface NotificationSettings {
  priceAlerts: boolean;
  trades: boolean;
  listings: boolean;
  delistings: boolean;
  system: boolean;
  inApp: boolean;
  telegram: boolean;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = TokenManager.getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Request failed with status ${res.status}`);
  }
  const json = await res.json();
  return json.data ?? json;
}

export const getNotifications = (userId: string, category: NotificationCategory | null) =>
  apiFetch<NotificationInbox>(
    `/notifications/${userId}${category ? `?category=${category.toLowerCase()}` : ""}`
  );

export const getUnreadCount = (userId: string) =>
  apiFetch<{ count: number }>(`/notifications/${userId}/unread-count`);

export const markNotificationsRead = (userId: string, id?: string) =>
  apiFetch<null>(`/notifications/${userId}/read`, {
    method: "POST",
    body: JSON.stringify(id ? { id } : {}),
  });

export const removeNotification = (userId: string, id: string) =>
  apiFetch<null>(`/notifications/${userId}/${encodeURIComponent(id)}`, { method: "DELETE" });

export const getNotificationSettings = (userId: string) =>
  apiFetch<NotificationSettings>(`/notifications/${userId}/settings`);

export const updateNotificationSettings = (userId: string, patch: Partial<NotificationSettings>) =>
  apiFetch<NotificationSettings>(`/notifications/${userId}/settings`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
